'use client'

import React from 'react'
import { Center, Flex, Stack, Text, Title } from '@mantine/core'
import { IconArrowRight, IconCalendarCheck, IconFlask, IconSignRight, IconStopwatch } from '@tabler/icons-react'
import { isMobile } from 'react-device-detect'

interface LabStepProcessProps {
    title?: string
}

interface LabStep {
    icon: React.ReactNode
    number: number
    title: string
    description: string
}

export function LabStepProcess({
    title = 'Complete insights in as little as 48 hours',
}: LabStepProcessProps): React.ReactElement {
    const steps: LabStep[] = [
        {
            icon: <IconCalendarCheck stroke={1} width={isMobile ? 54 : 50} height={50} color="#2D1AFF" />,
            number: 1,
            title: 'Book instantly',
            description: 'Click, book, done. Choose a convenient lab location near you. Transparent, up-front pricing.',
        },
        {
            icon: <IconFlask stroke={1} width={isMobile ? 54 : 50} color="#2D1AFF" />,
            number: 2,
            title: 'Quick lab visit',
            description:
                'Testing to fit your busy schedule, usually 15 minutes or less. Walk-in and appointments available.',
        },
        {
            icon: <IconStopwatch stroke={1} width={isMobile ? 54 : 50} color="#2D1AFF" />,
            number: 3,
            title: 'Results in 2-4 days',
            description: 'We fast-track the delivery of your health insights. No waiting weeks for answers.',
        },
        {
            icon: <IconSignRight stroke={1} width={isMobile ? 54 : 50} color="#2D1AFF" />,
            number: 4,
            title: 'Expert guidance',
            description: 'Our clinicians will recommend next steps. Or book a same-day visit for comprehensive care.',
        },
    ]

    return (
        <Stack gap={isMobile ? 28 : 80}>
            {renderTitle()}
            {renderLabSteps()}
        </Stack>
    )

    function renderTitle() {
        return (
            <Title
                fw={isMobile ? 500 : 400}
                c={'var(--GM-Text-Black, #181818)'}
                lh={isMobile ? '40px' : '54px'}
                fz={isMobile ? 36 : 48}
                lts={isMobile ? '-0.72px' : '-1.08px'}
                ta={isMobile ? 'left' : 'center'}
            >
                {title}
            </Title>
        )
    }

    function renderLabSteps() {
        return (
            <Flex
                direction={isMobile ? 'column' : { base: 'column', sm: 'row' }}
                gap={40}
                justify={'center'}
                align={'flex-start'}
                wrap={'nowrap'}
            >
                {steps.map((step, index) => {
                    const shouldShowArrow = !isMobile && index < steps.length - 1

                    return (
                        <React.Fragment key={step.number}>
                            {renderProcessStep(step)}
                            {shouldShowArrow && renderArrow()}
                        </React.Fragment>
                    )
                })}
            </Flex>
        )
    }

    function renderProcessStep(step: LabStep) {
        const { icon, description, title, number } = step
        return (
            <Stack
                gap={isMobile ? 6 : 24}
                justify={'center'}
                align={isMobile ? 'flex-start' : 'center'}
                maw={isMobile ? '' : '308px'}
                ta={isMobile ? 'left' : 'center'}
            >
                <Center
                    style={{
                        width: isMobile ? 108 : 150,
                        height: isMobile ? 108 : 150,
                        borderRadius: '50%',
                        backgroundColor: '#E1DBEF',
                    }}
                >
                    {React.cloneElement(icon as React.ReactElement, {
                        width: isMobile ? 54 : 75,
                        height: isMobile ? 54 : 75,
                    })}
                </Center>
                <Text c={'var(--GM-Text-Black, #181818)'} fz={isMobile ? 18 : 24} fw={500} lh={'32px'}>
                    {number}
                </Text>

                <Text
                    c={'var(--GM-Text-Black, #181818)'}
                    fz={isMobile ? 24 : 36}
                    fw={isMobile ? 500 : 400}
                    lh={isMobile ? '30px' : '30px'}
                    lts={isMobile ? '-0.48px' : '-0.72px'}
                >
                    {title}
                </Text>

                <Text
                    c={'var(--GM-Text-Black, #181818)'}
                    fz={isMobile ? 16 : 18}
                    fw={isMobile ? 400 : 500}
                    lh={isMobile ? '24px' : '130%'}
                >
                    {description}
                </Text>
            </Stack>
        )
    }

    function renderArrow() {
        return (
            <Flex align={'center'} justify={'center'} h={'150px'} className="hidden sm:flex">
                <IconArrowRight stroke={1} width={58} height={58} />
            </Flex>
        )
    }
}
